import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { X, Compass, Play } from 'lucide-react';

/**
 * Virtual Tour Modal
 * Displays a 360° panorama or embedded video tour for a property
 * If property.tour_url is set, that's used; otherwise falls back to image gallery
 */
export const VirtualTourModal = ({ open, onClose, property, lang = 'sq' }) => {
  const [activeImageIdx, setActiveImageIdx] = useState(0);

  if (!property) return null;

  const tourUrl = property.tour_url; // YouTube embed, Matterport, or 360° URL
  const images = property.images || [];
  const title = property.title?.[lang] || 'Property';

  const labels = lang === 'sq' ? {
    tour: 'Virtual Tour 360°',
    gallery: 'Galeria e Fotove',
    close: 'Mbyll',
  } : {
    tour: '360° Virtual Tour',
    gallery: 'Photo Gallery',
    close: 'Close',
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] bg-black border border-[#C5A05E]/30 text-white p-0 overflow-hidden" data-testid="virtual-tour-modal">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">
          {tourUrl ? labels.tour : labels.gallery} for {title}
        </DialogDescription>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-black via-slate-900 to-black">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#C5A05E] to-[#D4B06E] flex items-center justify-center">
              <Compass className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white text-lg">{title}</h3>
              <p className="text-xs text-[#C5A05E] uppercase tracking-wider">{tourUrl ? labels.tour : labels.gallery}</p>
            </div>
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10 rounded-full w-10 h-10 p-0"
            data-testid="virtual-tour-close"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="relative bg-black">
          {tourUrl ? (
            /* Embedded Virtual Tour (YouTube, Matterport, iframe) */
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              <iframe
                src={tourUrl}
                title="Virtual Tour"
                className="absolute top-0 left-0 w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; xr-spatial-tracking"
                allowFullScreen
              />
            </div>
          ) : images.length > 0 ? (
            /* Image Gallery Fallback */
            <div className="space-y-3 p-4">
              <div className="relative rounded-xl overflow-hidden bg-slate-900" style={{ paddingTop: '56.25%' }}>
                <img
                  src={images[activeImageIdx]?.startsWith('http') ? images[activeImageIdx] : `${process.env.REACT_APP_BACKEND_URL}${images[activeImageIdx]}`}
                  alt={title}
                  className="absolute top-0 left-0 w-full h-full object-cover"
                />
                {/* Navigation dots overlay */}
                {images.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 backdrop-blur-xl bg-black/60 px-4 py-2 rounded-full">
                    {images.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveImageIdx(idx)}
                        className={`w-2 h-2 rounded-full transition-all ${idx === activeImageIdx ? 'bg-[#C5A05E] w-8' : 'bg-white/40'}`}
                      />
                    ))}
                  </div>
                )}
              </div>
              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="grid grid-cols-6 gap-2">
                  {images.slice(0, 6).map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImageIdx(idx)}
                      className={`aspect-video rounded-lg overflow-hidden border-2 transition-all ${idx === activeImageIdx ? 'border-[#C5A05E] scale-105' : 'border-white/10 opacity-60 hover:opacity-100'}`}
                    >
                      <img
                        src={img?.startsWith('http') ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="p-16 text-center text-gray-400">
              <Compass className="w-16 h-16 mx-auto opacity-40 mb-4" />
              <p>No tour or images available</p>
            </div>
          )}
        </div>

        {/* Property Info Footer */}
        <div className="p-4 border-t border-white/10 bg-gradient-to-r from-black to-slate-900">
          <div className="flex items-center justify-between">
            <div className="flex gap-6 text-sm text-gray-300">
              <span>🛏 {property.bedrooms} {lang === 'sq' ? 'Dhoma' : 'Bedrooms'}</span>
              <span>🚿 {property.bathrooms} {lang === 'sq' ? 'Banjo' : 'Bathrooms'}</span>
              <span>📐 {property.area} m²</span>
            </div>
            <div className="text-2xl font-bold bg-gradient-to-r from-[#C5A05E] to-[#D4B06E] bg-clip-text text-transparent">
              €{property.price?.toLocaleString()}
              {property.type === 'rent' && <span className="text-sm text-gray-400">/{lang === 'sq' ? 'muaj' : 'mo'}</span>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
